/**
 * Automations — an audience plus a template plus a schedule.
 *
 * The point of this screen is answering "what is going out, to whom, and when" without opening
 * anything: the upcoming panel shows the next scheduled sends with a live recipient estimate, and
 * each row shows its template, audience and next run time.
 *
 * Enabling is deliberately gated. The server refuses to arm an automation whose template isn't
 * approved or whose variables aren't linked to CRM fields, and the reason is shown inline rather
 * than surfacing as a failed run hours later.
 */
import React, { useState } from 'react';
import {
  Zap, Power, Trash2, Edit3, Play, Clock, AlertTriangle, CalendarClock, History, Users, X, SkipForward, StopCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useListAutomationsQuery,
  useListUpcomingSendsQuery,
  useToggleAutomationMutation,
  useDeleteAutomationMutation,
  useRunAutomationNowMutation,
  usePreviewAutomationRunQuery,
  useCancelAutomationRunMutation,
  useListAutomationRunsQuery,
  useListAutomationSendsQuery,
} from '../../api/campaignsApi';

const RUN_STATUS_COLOR = {
  Running: 'b-blue', Completed: 'b-green', Failed: 'b-red',
  SkippedPaused: 'b-amber', SkippedEmpty: 'b-gray', Cancelled: 'b-gray',
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], {
  day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
}) : '—');

const LEAD_STATUS_LABEL = { FollowUpPending: 'Follow-up', NotConnected: 'Not Connected' };

/** How to describe an automation's schedule, since event triggers have no fixed time. */
const scheduleText = (a) => {
  if (a.trigger === 'DailyRecurring') return `daily at ${a.dailyAtIst} IST`;
  if (a.trigger === 'OneOffCampaign') return `once, ${fmt(a.scheduledAtUtc)}`;
  if (a.trigger === 'LeadCreated') return 'when a lead is created';
  if (a.trigger === 'LeadStatusChanged') {
    return `when status changes to ${LEAD_STATUS_LABEL[a.triggerLeadStatus] || a.triggerLeadStatus}`;
  }
  return '—';
};

const isEventTrigger = (a) => a.trigger === 'LeadCreated' || a.trigger === 'LeadStatusChanged';

const apiError = (e) => e?.data?.error?.message || 'Something went wrong.';

export default function AutomationsTab({ onEdit }) {
  const { data: automations, isFetching } = useListAutomationsQuery();
  const { data: upcoming } = useListUpcomingSendsQuery(10, { pollingInterval: 60000, skipPollingIfUnfocused: true });
  const [toggle] = useToggleAutomationMutation();
  const [remove] = useDeleteAutomationMutation();
  const [runNow, { isLoading: running }] = useRunAutomationNowMutation();
  const [historyFor, setHistoryFor] = useState(null);
  const [runConfirmFor, setRunConfirmFor] = useState(null); // automation object, or null

  const doToggle = async (a) => {
    try {
      const r = await toggle(a.id).unwrap();
      toast.success(r.isActive ? `${a.name} is now live.` : `${a.name} paused.`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Actually executing the run — called from the confirmation modal once staff has seen how many
  // will be skipped as already-messaged vs. how many new people will receive it.
  //
  // The server claims the run and returns immediately — the actual send continues in the
  // background (server-side, not tied to this tab), so a large campaign no longer needs this
  // browser connection to stay open for the 30+ minutes it might take. "Running" is therefore the
  // NORMAL response now, not just an edge case; progress shows up in the topbar's send indicator
  // and in Run history below, not in a final toast here.
  const doRunNow = async (a) => {
    try {
      const r = await runNow(a.id).unwrap();
      if (r.status === 'SkippedPaused') { toast.info('Nothing sent — WhatsApp sending is paused.'); return; }
      if (r.status === 'SkippedEmpty') { toast.info('Nobody matched the audience.'); return; }
      if (r.status === 'Running') {
        toast.success('Started — sending in the background. You can close this tab; check the topbar or Run history for progress.');
        return;
      }
      toast.success(`Sent ${r.sentCount}, failed ${r.failedCount}, skipped ${r.skippedCount}.`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRunConfirmFor(null);
    }
  };

  const doDelete = async (a) => {
    if (!window.confirm(`Delete the automation "${a.name}"?`)) return;
    try {
      await remove(a.id).unwrap();
      toast.success(`Deleted ${a.name}.`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <>
      {/* What's going out next — the "when will it be sent" view. */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">
          <h3><CalendarClock size={15} style={{ verticalAlign: -2, marginRight: 6 }} />Scheduled to send</h3>
          <span className="td-muted" style={{ fontSize: '0.78rem' }}>
            Recipient counts are live estimates — the audience is re-checked at send time.
          </span>
        </div>
        <div className="card-body">
          {(upcoming ?? []).map((u) => (
            <div key={u.automationId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
              <span className="badge b-blue"><Clock size={10} /> {fmt(u.nextRunAtUtc)}</span>
              <strong style={{ fontSize: '0.85rem' }}>{u.automationName}</strong>
              <span className="td-muted td-mono" style={{ fontSize: '0.78rem' }}>{u.templateName}</span>
              <span className="td-muted" style={{ fontSize: '0.78rem' }}>→ {u.audienceName}</span>
              <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                <Users size={12} />
                <strong style={{ fontSize: '0.85rem' }}>{u.estimatedRecipients.toLocaleString()}</strong>
                {u.estimateNote && (
                  <span title={u.estimateNote}><AlertTriangle size={12} style={{ color: 'var(--danger)' }} /></span>
                )}
              </span>
            </div>
          ))}
          {(upcoming ?? []).length === 0 && (
            <div className="td-muted" style={{ fontSize: '0.82rem' }}>
              Nothing scheduled. Switch an automation on below and its next send will appear here.
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3><Zap size={15} style={{ verticalAlign: -2, marginRight: 6 }} />Automations</h3>
        </div>
        <div className="card-body">
          {isFetching && <div className="skeleton" style={{ height: 60 }} />}
          {(automations ?? []).map((a) => (
            <div key={a.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', opacity: a.isActive ? 1 : 0.7 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span className={`badge ${a.isActive ? 'b-green' : 'b-gray'}`}>{a.isActive ? 'Live' : 'Paused'}</span>
                <strong style={{ fontSize: '0.85rem' }}>{a.name}</strong>
                <span className="badge b-purple td-mono">{a.templateName}</span>
                {a.audienceName && <span className="td-muted" style={{ fontSize: '0.8rem' }}>→ {a.audienceName}</span>}
                <span className="td-muted" style={{ fontSize: '0.8rem' }}>{scheduleText(a)}</span>
                {a.isActive && a.nextRunAtUtc && (
                  <span className="badge b-blue" title="Next send"><Clock size={10} /> {fmt(a.nextRunAtUtc)}</span>
                )}
                {a.isActive && isEventTrigger(a) && (
                  <span className="badge b-teal" title="Fires immediately when the event happens, no fixed schedule">
                    <Zap size={9} /> live
                  </span>
                )}
                {a.lastRunAt && (
                  <span className="td-muted" style={{ fontSize: '0.76rem' }}>
                    last fired {fmt(a.lastRunAt)} · {a.lastRunSentCount} sent
                  </span>
                )}
                <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 4 }}>
                  <button className="btn-icon-sm" title="Run history"
                    onClick={() => setHistoryFor(historyFor === a.id ? null : a.id)}><History size={14} /></button>
                  {!isEventTrigger(a) && (
                    <button className="btn-icon-sm" title="Send now" disabled={running || !a.canActivate}
                      onClick={() => setRunConfirmFor(a)}><Play size={14} /></button>
                  )}
                  <button className="btn-icon-sm" title={a.isActive ? 'Pause' : 'Switch on'}
                    onClick={() => doToggle(a)}>
                    <Power size={14} color={a.isActive ? 'var(--green-700)' : undefined} />
                  </button>
                  <button className="btn-icon-sm" title="Edit" onClick={() => onEdit(a)}><Edit3 size={14} /></button>
                  <button className="btn-icon-sm" title="Delete" onClick={() => doDelete(a)}><Trash2 size={14} /></button>
                </span>
              </div>

              {a.blockedReason && (
                <div style={{ fontSize: '0.78rem', color: 'var(--danger)', marginTop: 4, display: 'flex', gap: 6 }}>
                  <AlertTriangle size={13} /> {a.blockedReason}
                </div>
              )}

              {historyFor === a.id && <RunHistory automationId={a.id} />}
            </div>
          ))}

          {!isFetching && (automations ?? []).length === 0 && (
            <div className="empty-state">
              <h3>No automations yet</h3>
              <p>Pair an approved template with an audience and choose when it should send — for example,
                a callback message every morning to patients who haven’t been in for 15 to 20 days.</p>
            </div>
          )}
        </div>
      </div>

      {runConfirmFor && (
        <RunConfirmModal
          automation={runConfirmFor}
          running={running}
          onClose={() => setRunConfirmFor(null)}
          onConfirm={() => doRunNow(runConfirmFor)}
        />
      )}
    </>
  );
}

/**
 * Shown before every manual run. Backend now excludes anyone this automation has EVER messaged
 * (any prior run, not just today's) before a send starts — that guarantee is unconditional and
 * can't be turned off from here. The checkbox below is a confirmation gesture, not a live toggle:
 * staff must tick it to acknowledge how many are being skipped before Send now is enabled, so
 * nobody clicks through without seeing the split between "already messaged" and "will send to".
 */
function RunConfirmModal({ automation, running, onClose, onConfirm }) {
  const { data: p, isFetching, isError } = usePreviewAutomationRunQuery(automation.id);
  const [skipConfirmed, setSkipConfirmed] = useState(false);

  const hasAlready = (p?.alreadyMessaged ?? 0) > 0;
  // Nothing to confirm when nobody's been messaged yet — don't force a pointless click. Also
  // nothing to send once wouldSend hits 0 (everyone currently matching has already been reached).
  const canSend = !isFetching && (!p || p.wouldSend > 0) && (!hasAlready || skipConfirmed);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <h2>Send now — {automation.name}</h2>
          <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body" style={{ display: 'grid', gap: 14 }}>
          {isFetching && <div className="skeleton" style={{ height: 90 }} />}

          {isError && !isFetching && (
            <div className="td-muted" style={{ fontSize: '0.85rem' }}>
              Couldn’t estimate recipients right now. You can still send — the real audience is
              always re-checked at send time.
            </div>
          )}

          {p && !isFetching && (
            <>
              <div className="kpi-grid">
                <div className="kpi-card" style={{ '--kpi-accent': 'var(--blue-700)' }}>
                  <div className="kpi-label">Currently match</div>
                  <div className="kpi-value">{p.totalMatched.toLocaleString()}</div>
                </div>
                <div className="kpi-card" style={{ '--kpi-accent': 'var(--amber-500)' }}>
                  <div className="kpi-label">Already messaged</div>
                  <div className="kpi-value">{p.alreadyMessaged.toLocaleString()}</div>
                </div>
                <div className="kpi-card" style={{ '--kpi-accent': 'var(--green-700)' }}>
                  <div className="kpi-label">Will send to</div>
                  <div className="kpi-value">{p.wouldSend.toLocaleString()}</div>
                </div>
              </div>

              {hasAlready && (
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
                  <input type="checkbox" checked={skipConfirmed} style={{ marginTop: 3 }}
                    onChange={(e) => setSkipConfirmed(e.target.checked)} />
                  <span style={{ fontSize: '0.84rem' }}>
                    <SkipForward size={13} style={{ verticalAlign: -2, marginRight: 4 }} />
                    Skip the <strong>{p.alreadyMessaged.toLocaleString()}</strong> {p.alreadyMessaged === 1 ? 'contact' : 'contacts'} already
                    sent this template, and send it now to the <strong>{p.wouldSend.toLocaleString()}</strong> remaining.
                  </span>
                </label>
              )}

              {p.capExceeded && (
                <div style={{ fontSize: '0.8rem', color: 'var(--danger)', display: 'flex', gap: 6 }}>
                  <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} />
                  The real audience may be larger than shown — this is a floor, not an exact count.
                </div>
              )}

              {p.wouldSend === 0 && (
                <div className="td-muted" style={{ fontSize: '0.82rem' }}>
                  Nobody new to send to — everyone currently matching has already been messaged.
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={running}>Cancel</button>
          <button className="btn btn-primary" onClick={onConfirm} disabled={running || !canSend}>
            <Play size={15} /> {running ? 'Sending…' : 'Send now'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RunHistory({ automationId }) {
  // Polls while open so a run's sent/failed/skipped counts visibly climb in place — the send
  // itself runs server-side regardless, this is just keeping the number on screen honest.
  const { data: runs, isFetching } = useListAutomationRunsQuery(
    { id: automationId, page: 1, pageSize: 10 }, { pollingInterval: 8000 },
  );
  const [openRun, setOpenRun] = useState(null);
  const [cancelRun, { isLoading: cancelling }] = useCancelAutomationRunMutation();
  const doCancelRun = async (runId) => {
    try {
      await cancelRun(runId).unwrap();
      toast.info('Cancelling — it will stop after the recipient currently in flight.');
    } catch (e) {
      toast.error(e?.data?.error?.message || 'Could not cancel.');
    }
  };

  return (
    <div style={{ marginTop: 8, marginLeft: 12, paddingLeft: 10, borderLeft: '2px solid var(--border)' }}>
      {isFetching && <div className="skeleton" style={{ height: 30 }} />}
      {(runs?.items ?? []).map((r) => (
        <div key={r.id}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: '0.8rem' }}>
            <span className={`badge ${RUN_STATUS_COLOR[r.status] || 'b-gray'}`}>{r.status}</span>
            <span className="td-muted td-mono">{r.runKey}</span>
            <span className="td-muted">
              {r.sentCount} sent · {r.failedCount} failed · {r.skippedCount} skipped of {r.audienceCount} matched
            </span>
            {r.truncatedByCap && (
              <span className="badge b-amber" title={r.note}><AlertTriangle size={9} /> capped</span>
            )}
            {r.status === 'Running' && (
              <button className="btn-icon-sm" style={{ marginLeft: 'auto' }}
                title="Stop this send — already-messaged people keep their claim"
                disabled={cancelling} onClick={() => doCancelRun(r.id)}>
                <StopCircle size={13} color="var(--danger, #dc2626)" />
              </button>
            )}
            <button className="btn-icon-sm" style={{ marginLeft: r.status === 'Running' ? 0 : 'auto' }} title="Per-recipient detail"
              onClick={() => setOpenRun(openRun === r.id ? null : r.id)}><Users size={13} /></button>
          </div>
          {r.note && <div className="td-muted" style={{ fontSize: '0.74rem', marginLeft: 4 }}>{r.note}</div>}
          {openRun === r.id && <RunSends runId={r.id} />}
        </div>
      ))}
      {!isFetching && (runs?.items ?? []).length === 0 && (
        <div className="td-muted" style={{ fontSize: '0.78rem' }}>Hasn’t run yet.</div>
      )}
    </div>
  );
}

function RunSends({ runId }) {
  const { data: sends } = useListAutomationSendsQuery({ runId, page: 1, pageSize: 50 });
  return (
    <div className="tbl-wrap" style={{ maxHeight: 200, overflowY: 'auto', margin: '4px 0 8px' }}>
      <table>
        <thead><tr><th>Recipient</th><th>Phone</th><th>Result</th></tr></thead>
        <tbody>
          {(sends?.items ?? []).map((s) => (
            <tr key={s.id}>
              <td>{s.recipientName || '—'}</td>
              <td className="td-mono">{s.recipientPhone}</td>
              <td>
                <span className={`badge ${s.status === 'Sent' ? 'b-green' : s.status === 'Failed' ? 'b-red' : 'b-gray'}`}>
                  {s.status}
                </span>
                {s.failureReason && (
                  <span className="td-muted" style={{ fontSize: '0.74rem', marginLeft: 6 }}>{s.failureReason}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
